import { useEffect } from "react";
import { Auth } from "aws-amplify";

import {
    Authenticator,
    useAuthenticator,
    View,
    useTheme,
    Text,
    Heading,
    Button,
    Flex,
    Image,
} from "@aws-amplify/ui-react";

import styled from "styled-components";

const hostname =
    typeof window !== "undefined" ? window.location.hostname : "server";

// reCAPTCHA Enterprise site key. Public by design (it is rendered into the page),
// so it is a plain build-time env var rather than a secret. When unset — local
// dev, or before the key is provisioned — the widget is skipped entirely and
// signup proceeds without a token.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const RECAPTCHA_ACTION = "signup";

/**
 * Load the reCAPTCHA Enterprise script once, resolving when grecaptcha is ready.
 *
 * Resolves to false rather than rejecting on any failure (blocked by an ad
 * blocker, offline, CSP). A captcha that cannot load must not be able to stop
 * people signing up — the backend trigger decides what to do about a missing
 * token, and may reject tokenless sign-ups when RECAPTCHA_REQUIRE=1.
 */
let recaptchaReady = null;
function loadRecaptcha() {
    if (!RECAPTCHA_SITE_KEY || typeof window === "undefined") {
        return Promise.resolve(false);
    }
    if (recaptchaReady) return recaptchaReady;

    recaptchaReady = new Promise((resolve) => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src =
            "https://www.google.com/recaptcha/enterprise.js?render=" +
            encodeURIComponent(RECAPTCHA_SITE_KEY);
        script.async = true;
        script.onload = () => resolve(Boolean(window.grecaptcha));
        script.onerror = () => {
            // Do not cache the failure: one transient blip would otherwise
            // disable tokens until a full page reload.
            recaptchaReady = null;
            resolve(false);
        };
        document.head.appendChild(script);
    });
    return recaptchaReady;
}

/** Best-effort token. Returns "" on any failure; never throws. */
const RECAPTCHA_TOKEN_TIMEOUT_MS = 3000;

async function getRecaptchaToken() {
    try {
        // Everything below is raced against a deadline. script.onload/onerror do
        // NOT fire when a request is blackholed rather than refused (corporate
        // proxies, some DNS blocklists), and grecaptcha.execute has no timeout of
        // its own — so without this the promise can never settle, leaving the
        // Authenticator stuck in "pending" with the submit button disabled and no
        // error shown. A missing token is harmless; a hung form is not.
        const token = new Promise((resolve) => {
            loadRecaptcha().then((ok) => {
                if (!ok || !window.grecaptcha || !window.grecaptcha.enterprise) {
                    resolve("");
                    return;
                }
                window.grecaptcha.enterprise.ready(() => {
                    window.grecaptcha.enterprise
                        .execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION })
                        .then((t) => resolve(t || ""))
                        .catch(() => resolve(""));
                });
            }).catch(() => resolve(""));
        });
        const deadline = new Promise((resolve) =>
            setTimeout(() => resolve(""), RECAPTCHA_TOKEN_TIMEOUT_MS),
        );
        return await Promise.race([token, deadline]);
    } catch (err) {
        return "";
    }
}

/**
 * Sign-up handler that attaches the reCAPTCHA token as clientMetadata.
 *
 * clientMetadata is the ONLY channel by which a PreSignUp Lambda can see a
 * captcha token — Cognito passes nothing captcha-shaped on its own. The
 * gcPreSignUpRecaptcha trigger reads `clientMetadata.recaptchaToken`.
 *
 * Note the Hosted UI at 1stinfluencer.auth.us-east-1.amazoncognito.com cannot
 * send clientMetadata at all, so that path always arrives tokenless. The trigger
 * may reject tokenless sign-ups when RECAPTCHA_REQUIRE=1; otherwise it fails open.
 */
async function handleSignUp(formData) {
    const { username, password, attributes } = formData;
    const recaptchaToken = await getRecaptchaToken();
    return Auth.signUp({
        username,
        password,
        attributes,
        autoSignIn: { enabled: true },
        ...(recaptchaToken ? { clientMetadata: { recaptchaToken } } : {}),
    });
}

const currentYear = new Date().getFullYear();

/*

const isRegistrationDisabled = hostname.includes("1stinfluencer") ? true : false;

const toMainPage = () => {
  window.location.href = "https://1stinfluencer.com";
}

{isRegistrationDisabled ?
  <Button
    fontWeight="normal"
    onClick={toMainPage}
    size="small"
    variation="link"
  >
    Start from the main page
  </Button>
  :
  <Button
    fontWeight="normal"
    onClick={toSignUp}
    size="small"
    variation="link"
  >
    Sign up
  </Button>
}


*/

const cssFor1stInfluencer = hostname.includes("1stinfluencer")
    ? `
  border:none;
  box-shadow: none;
  `
    : ``;
const StyledAuthenticator = styled(Authenticator)`
    .amplify-tabs {
        display: none;
    }
    .amplify-authenticator {
        border: none;
    }
    [data-amplify-router] {
        ${cssFor1stInfluencer}
    }
`;

export function Register(props) {
    const components = {
        Header() {
            const { tokens } = useTheme();

            return (
                <Flex
                    textAlign="center"
                    paddingTop={tokens.space.medium}
                    paddingBottom={tokens.space.medium}
                    justifyContent="center"
                    alignItems="center"
                    alignContent="center"
                    wrap="nowrap"
                >
                    {hostname.includes("1stinfluencer") ? (
                        <Image
                            alt="1stInfluencer logo"
                            src="/1stinfluencer/1stinfluencer-logo.png"
                            padding="0 10px"
                        />
                    ) : (
                        <Text
                            fontWeight={600}
                            fontSize="2.7em"
                            color={"#414c5a"}
                        >
                            LinkedIn Business
                            <br />
                            Influencer Marketplace
                        </Text>
                    )}
                </Flex>
            );
        },

        Footer() {
            const { tokens } = useTheme();

            return (
                <View textAlign="center" padding={tokens.space.large}>
                    {hostname.includes("1stinfluencer") ? (
                        <Text color={tokens.colors.neutral[80]}>
                            {currentYear} &copy; All Rights Reserved
                        </Text>
                    ) : (
                        <Text color={tokens.colors.neutral[80]}>
                            2019-{currentYear} &copy; All Rights Reserved
                        </Text>
                    )}
                </View>
            );
        },

        SignIn: {
            Header() {
                const { tokens } = useTheme();

                return (
                    <Heading
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={3}
                        color={"#414c5a"}
                    >
                        Sign in to account
                    </Heading>
                );
            },
            Footer() {
                const { toResetPassword, toSignUp } = useAuthenticator();

                return (
                    <View textAlign="center">
                        <Button
                            fontWeight="normal"
                            onClick={toResetPassword}
                            size="small"
                            variation="link"
                        >
                            Forgot password?
                        </Button>
                        <br />
                        <br />
                        New here?
                        <br />
                        <Button
                            fontWeight="normal"
                            onClick={toSignUp}
                            size="small"
                            variation="link"
                        >
                            Sign up
                        </Button>
                        <br />
                        <br />
                    </View>
                );
            },
        },

        SignUp: {
            Header() {
                const { tokens } = useTheme();

                return (
                    <Heading
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={3}
                        color={"#414c5a"}
                    >
                        Please register to start
                    </Heading>
                );
            },
            Footer() {
                const { toResetPassword, toSignIn } = useAuthenticator();

                return (
                    <View textAlign="center">
                        <Button
                            fontWeight="normal"
                            onClick={toResetPassword}
                            size="small"
                            variation="link"
                        >
                            Forgot password?
                        </Button>
                        <br />
                        <br />
                        <Button
                            fontWeight="normal"
                            onClick={toSignIn}
                            size="small"
                            variation="link"
                        >
                            Back to Sign In
                        </Button>
                        <br />
                        <br />
                        {/* Google requires this notice wherever the reCAPTCHA badge is
                            hidden (see .grecaptcha-badge in index.css). */}
                        <View as="p" fontSize="0.7rem" color="#8a94a6" padding="0 1.5rem 1rem" margin="0">
                            This site is protected by reCAPTCHA and the Google{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>Privacy Policy</a> and{" "}
                            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>Terms of Service</a> apply.
                        </View>
                    </View>
                );
            },
        },
        ConfirmSignUp: {
            Header() {
                const { tokens } = useTheme();
                return (
                    <Heading
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={3}
                        color={"#414c5a"}
                    >
                        Enter the pin from email:
                    </Heading>
                );
            },
            Footer() {},
        },
        SetupTOTP: {
            Header() {
                const { tokens } = useTheme();
                return (
                    <Heading
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={3}
                        color={"#414c5a"}
                    >
                        Enter Information:
                    </Heading>
                );
            },
            Footer() {
                return <Text></Text>;
            },
        },
        ConfirmSignIn: {
            Header() {
                const { tokens } = useTheme();
                return (
                    <Heading
                        padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
                        level={3}
                        color={"#414c5a"}
                    >
                        Enter Information:
                    </Heading>
                );
            },
            Footer() {
                return <Text></Text>;
            },
        },
        ResetPassword: {
            Header() {
                return (
                    <Heading padding={`0 0 0 0`} level={3} color={"#414c5a"}>
                        Reset password
                    </Heading>
                );
            },
            Footer() {},
        },
        ConfirmResetPassword: {
            Header() {
                return (
                    <Heading padding={`0 0 0 0`} level={3} color={"#414c5a"}>
                        Set new password
                    </Heading>
                );
            },
            Footer() {},
        },
    };

    const formFields = {
        signIn: {
            username: {
                labelHidden: false,
                placeholder: "e.g. john@business.com",
            },
        },
        signUp: {
            name: {
                labelHidden: false,
                label: "First Name",
                placeholder: "e.g. John",
                isRequired: true,
                order: 1,
            },
            email: {
                labelHidden: false,
                label: "Email",
                placeholder: "e.g. john@business.com",
                isRequired: true,
                order: 2,
            },
            password: {
                labelHidden: false,
                label: "Password (6 or more characters)",
                placeholder: "******",
                isRequired: false,
                order: 3,
            },
            confirm_password: {
                labelHidden: true,
                placeholder: "******",
                order: 4,
            },
            "custom:domain": {
                defaultValue: hostname,
                type: "hidden",
                custom: true,
                isRequired: true,
                order: 6,
            },
        },
        forceNewPassword: {
            password: {
                labelHidden: false,
                placeholder: "Enter your Password:",
            },
        },
        resetPassword: {
            username: {
                labelHidden: false,
                label: "Email",
                placeholder: "e.g. john@business.com",
                isRequired: true,
            },
        },
        confirmResetPassword: {
            confirmation_code: {
                labelHidden: false,
                placeholder: "6-digit code",
                label: "Reset code from email",
                isRequired: true,
            },
            password: {
                labelHidden: false,
                label: "Password (6 or more characters)",
                placeholder: "******",
                isRequired: true,
            },
            confirm_password: {
                labelHidden: true,
                placeholder: "******",
                isRequired: true,
            },
        },
        setupTOTP: {
            QR: {
                totpIssuer: "test issuer",
                totpUsername: "amplify_qr_test_user",
            },
            confirmation_code: {
                labelHidden: false,
                label: "New Label",
                placeholder: "Enter your Confirmation Code:",
                isRequired: false,
            },
        },
        confirmSignIn: {
            confirmation_code: {
                labelHidden: false,
                label: "New Label",
                placeholder: "Enter your Confirmation Code:",
                isRequired: false,
            },
        },
    };

    useEffect(() => {
        setTimeout(() => {
            const input = document.querySelector('input[name="email"]');

            const urlParams = new URLSearchParams(window.location.search);

            if (input && urlParams.has("email")) {
                input.setAttribute("value", urlParams.get("email"));
            }
        });
    }, []);

    // Preload the script so a token is usually ready by submit. loadRecaptcha
    // memoizes its promise, so the later call from handleSignUp is free.
    useEffect(() => {
        if (props.initialState === "signUp") loadRecaptcha().catch(() => {});
    }, [props.initialState]);

    return (
        <StyledAuthenticator
            formFields={formFields}
            components={components}
            initialState={props.initialState}
            services={{ handleSignUp }}
        ></StyledAuthenticator>
    );
}
